import { useIsFocused } from 'expo-router/react-navigation';
import { useEffect, useState } from 'react';

import { useLibraryFilters } from '../../state/libraryFiltersStore';

import { useIsTabRootSceneSelected } from '@/components/TabRootList';
import { Heading } from '@/components/heading';
import { Button, ButtonText } from '@/components/ui/button';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { useBreakpointValue } from '@/components/ui/utils/use-break-point-value';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import { useUserScope } from '@/hooks/useUserScope';
import { getPlatformStorage } from '@/util/platformStorage';
import { Sentry } from '@/util/sentry';

export type CommunityDisclaimerModalProps = {
  isOpen?: boolean;
};

const getStorage = (userId?: string) => getPlatformStorage(userId, 'local');

export const getCommunityDisclaimerVisibility = async (userId?: string) => {
  try {
    return !(await getStorage(userId).getItem(STORAGE_KEYS.COMMUNITY_DISCLAIMER_SHOWN));
  } catch (e) {
    console.error(e);

    Sentry.captureException(e);

    return false;
  }
};

export function CommunityDisclaimerModal({ isOpen }: CommunityDisclaimerModalProps) {
  const { userId } = useUserScope();

  const setShowCommunityDisclaimerModal = useLibraryFilters(
    (state) => state.setShowDisclaimerModal
  );

  const modalSize =
    useBreakpointValue({
      default: 'md',
      md: 'sm',
    }) || 'sm';

  return (
    <Modal isOpen={isOpen} size={modalSize}>
      <ModalBackdrop />

      <ModalContent className="max-h-[70vh]">
        <ModalHeader>
          <Heading size="h2-lg">Welcome to the Inquiry Library</Heading>
        </ModalHeader>

        <ModalBody>
          <Text className="mb-2">
            This content is intended exclusively for healthcare professionals actively treating
            patients. Unauthorized use, including screenshots, downloading, or sharing for purposes
            unrelated to patient care or education, is strictly prohibited.
          </Text>

          <Text>By pressing 'I agree', I certify that I will comply with these terms.</Text>
        </ModalBody>

        <ModalFooter className="mt-6 w-full flex-row items-center justify-center gap-4">
          <Button
            onPress={async () => {
              try {
                await getStorage(userId).setItem(
                  STORAGE_KEYS.COMMUNITY_DISCLAIMER_SHOWN,
                  new Date().toISOString()
                );
              } catch (e) {
                console.error(e);

                Sentry.captureException(e);
              }

              setShowCommunityDisclaimerModal(false);
            }}
            variant="outline">
            <ButtonText>I agree</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * Shows the disclaimer on a reader's first visit to the Inquiry Library.
 *
 * It asks only while the reader is actually looking at the library: its screen is focused, and
 * on a tab root its section is the selected one. A tab root stays mounted behind the others
 * and keeps its visited sections mounted behind the selected one, and an RN Modal covers the
 * whole app, so an instance that is merely mounted would open over whatever the reader is on.
 *
 * An instance that is not being looked at does not read storage or touch the shared flag
 * either. Several can be mounted at once, the list and an open post among them, and each one
 * writing the flag made its meaning depend on which resolved last.
 *
 * The answer is kept with the user it was read for. The flag is shared and outlives a change
 * of user, so on its own it would show the previous user's answer for the moment before the
 * new one's is read.
 *
 * Mounted by whichever layout hosts the list, the web layout with its sidebar or the bare native
 * Library list, so the first visit asks once wherever it lands.
 */
export function CommunityDisclaimer() {
  const { userId } = useUserScope();

  const isOpen = useLibraryFilters((state) => state.showDisclaimerModal);

  const setIsOpen = useLibraryFilters((state) => state.setShowDisclaimerModal);

  const [answeredFor, setAnsweredFor] = useState<{ userId: string | undefined } | null>(null);

  const isFocused = useIsFocused();

  const isSceneSelected = useIsTabRootSceneSelected();

  const isBeingViewed = isFocused && isSceneSelected;

  useEffect(() => {
    if (!isBeingViewed) {
      return;
    }

    let isCurrent = true;

    getCommunityDisclaimerVisibility(userId)
      .then((shouldShow) => {
        if (!isCurrent) {
          return;
        }

        setIsOpen(shouldShow);

        setAnsweredFor({ userId });
      })
      .catch(() => undefined);

    return () => {
      isCurrent = false;
    };
  }, [userId, isBeingViewed, setIsOpen]);

  const hasAnswerForThisUser = answeredFor !== null && answeredFor.userId === userId;

  return <CommunityDisclaimerModal isOpen={isOpen && isBeingViewed && hasAnswerForThisUser} />;
}
